import { useCallback, useMemo } from 'react';
import { useCustomerCreate, useSettings } from '@backpackjs/storefront';
import { useRouter } from 'next/router';

import { Spinner } from '~/snippets';
import { useDataLayerActions } from '~/hooks';

export function RegisterForm() {
  const {
    customerCreate,
    errors,
    finished,
    reset,
    resetTimer,
    setErrors,
    started,
    success,
  } = useCustomerCreate();
  const settings = useSettings();
  const { route } = useRouter();
  const { sendRegisterEvent } = useDataLayerActions();

  const { heading, pageHeading } = { ...settings?.account?.register };

  const buttonText = useMemo(() => {
    if (finished) {
      resetTimer.current = setTimeout(() => {
        if (reset) reset();
        clearTimeout(resetTimer.current);
      }, 3000);
      return success ? 'Account Created' : 'Failed';
    }
    return started ? 'Creating Account' : 'Create Account';
  }, [finished, started, success]);

  const handleCustomerCreate = useCallback(
    async (event) => {
      event.preventDefault();
      if (started) return;

      const { firstName, lastName, email, password, passwordConfirm } =
        event.target;

      if (password.value !== passwordConfirm.value) {
        setErrors(['Passwords do not match. Please try again.']);

        if (finished) {
          resetTimer.current = setTimeout(() => {
            if (reset) reset();
            clearTimeout(resetTimer.current);
          }, 3000);
        }
        return;
      }

      await customerCreate({
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
        password: password.value,
        acceptsMarketing: false,
      });

      sendRegisterEvent();
    },
    [finished, reset, started]
  );

  return (
    <div className="rounded border border-border px-3 py-6 md:px-6 md:py-10">
      {route === '/account/register' && !pageHeading ? (
        <h1 className="text-title-h3 mb-6 text-center">{heading}</h1>
      ) : (
        <h2 className="text-title-h3 mb-6 text-center">{heading}</h2>
      )}

      <form
        className="mx-auto flex w-full max-w-[25rem] flex-col gap-4"
        onSubmit={handleCustomerCreate}
      >
        <label htmlFor="firstName">
          <span className="input-label">First Name</span>
          <input
            className="input-text"
            name="firstName"
            placeholder="First Name"
            required
            type="text"
          />
        </label>

        <label htmlFor="lastName">
          <span className="input-label">Last Name</span>
          <input
            className="input-text"
            name="lastName"
            placeholder="Last Name"
            required
            type="text"
          />
        </label>

        <label htmlFor="email">
          <span className="input-label">Email</span>
          <input
            className="input-text"
            name="email"
            placeholder="email@email.com"
            required
            type="email"
          />
        </label>

        <label htmlFor="password">
          <span className="input-label">Password</span>
          <input
            className="input-text"
            name="password"
            placeholder="••••••••"
            required
            type="password"
          />
        </label>

        <label htmlFor="passwordConfirm">
          <span className="input-label">Confirm Password</span>
          <input
            className="input-text"
            name="passwordConfirm"
            placeholder="••••••••"
            required
            type="password"
          />
        </label>

        <button
          aria-label="Create Account"
          className={`btn-primary mt-3 w-full min-w-[10rem] self-center md:w-auto  ${
            started ? 'cursor-default' : ''
          }`}
          type="submit"
        >
          {started ? <Spinner width="20" /> : buttonText}
        </button>
      </form>

      {errors?.length > 0 && (
        <ul className="mt-4 flex flex-col items-center gap-1">
          {errors.map((error, index) => {
            return (
              <li key={index} className="text-center text-sm text-red-500">
                {error}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

RegisterForm.displayName = 'RegisterForm';
